import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Link} from 'react-router-dom'
import {useFormik} from 'formik'
import {useAuth} from '../../modules/auth'
import {ModeleValueLabel} from '../../../components/Models'
import {getAllActions, getStatutActions, getTypeActions} from '../../../components/requests'
import {shouldDisplayFonctionnalite} from '../../../components/helpers'
import {KTIcon} from '../../../_metronic/helpers'
import {origineFiltreMesActions} from '../../../components/Data'
import {DateInput} from '../../../components/inputs/DateInput'
import { LoadingData } from '../../../components/LoadingData'
import KanbanView from './components/Kanban/KanbanView'
import {TableView} from './components/TableView'
import ListView from './components/Liste/ListView'
import {Pagination} from '../../../components/Pagination'
import {useLocation} from 'react-router'

export type ViewType = 'List' | 'Kanban' | 'Table'

export type SearchFilters = {
  allSearch: string
  statut: string
  type: string
  origine: string
  dateMin: string
  dateMax: string
}

export type ActionStatut = {
  code: string
  libelle?: string
}

export type ActionType = {
  id?: number | string
  code?: string
  libelle?: string
}

export type ActionUser = {
  id?: number | string
  prenom?: string
  nom?: string
}

export type ActionClient = {
  id?: number | string
  nomSociete?: string
  prenom?: string
  nom?: string
}

export type ActionDemande = {
  id?: number | string
  client?: {
    id?: number | string
  }
}

export type Action = {
  id: number | string
  titre?: string
  commentaire?: string
  statut?: ActionStatut
  type?: ActionType
  origine?: string
  dateLecture?: string | null
  dateEcheance?: string | null
  createdAt?: string | null
  prioritaire?: boolean
  lu?: boolean | null
  client?: ActionClient
  assigneeA?: ActionUser
  demande?: ActionDemande
  // Tâche d'un dossier SAV (qualification, réception, contrôle, remise en stock…)
  savDossier?: {id: number; numero: string} | null
  [key: string]: unknown
}

export type CacheEntry = {
  data: Action[]
  page: number
  pages: number
  total: number
}

const PAGE_SIZE = 25

interface LocationState {
  view?: 'List' | 'Kanban' | 'Table' | null | undefined
  filters?: any
}

const MesActionsListPage = () => {
  const {currentUser} = useAuth()
  const location = useLocation()
  const ls = location.state as LocationState | null
  const [actions, setActions] = useState<Action[]>([])
  const [view, setView] = useState<ViewType>(ls?.view ?? 'List')
  const [nombreDePages, setNombreDePages] = useState<number>(0)
  const [totalPages, setTotalPages] = useState<number>(0)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [loading, setLoading] = useState<boolean>(false)
  const [statutActions, setStatutActions] = useState<Array<ModeleValueLabel>>([])
  const [typeActions, setTypeActions] = useState<Array<ModeleValueLabel>>([])
  const [mesActions, setMesActions] = useState<boolean>(ls?.filters?.mesActions ?? false)
  const [dateMin, setDateMin] = useState(ls?.filters?.dateMin ?? null)
  const [dateMax, setDateMax] = useState(ls?.filters?.dateMax ?? null)
  const [filters, setFilters] = useState<SearchFilters>(ls?.filters ?? {
    allSearch: '',
    statut: '',
    type: '',
    origine: '',
    dateMin: '',
    dateMax: '',
  })
  const actionsCache = useRef<Map<string, CacheEntry>>(new Map())
  const requestIdRef = useRef<number>(0)

  const formik = useFormik<SearchFilters>({
    initialValues: ls?.filters ?? {
      allSearch: '',
      statut: '',
      type: '',
      origine: '',
      dateMin: '',
      dateMax: '',
    },

    onSubmit: (values) => {
      setCurrentPage(1)
      setFilters({
        allSearch: values.allSearch || '',
        statut: values.statut || '',
        type: values.type || '',
        origine: values.origine || '',
        dateMin: values.dateMin || '',
        dateMax: values.dateMax || '',
      })
    },
  })

  const apiStatut = useMemo(() => {
    if (view === 'List') {
      return 'NOUVELLE,ENCOURS'
    }

    if (filters.statut) {
      return filters.statut
    }

    return ''
  }, [view, filters.statut])
  const apiMesActions = view === 'List' || view === 'Kanban' ? mesActions : false
  const cacheKey = useMemo(() => {
    return JSON.stringify({
      view,
      page: currentPage,
      limit: PAGE_SIZE,
      search: filters.allSearch,
      statut: apiStatut,
      type: filters.type,
      origine: filters.origine,
      dateMin: filters.dateMin,
      dateMax: filters.dateMax,
      mesActions: apiMesActions,
    })
  }, [view, currentPage, filters, apiStatut, apiMesActions])

  const updateActionEverywhere = useCallback(
    (updatedAction) => {
      const updatedActionId = String(updatedAction.id)
      setActions((prevActions) => {
        const exists = prevActions.some((item) => String(item.id) === updatedActionId)

        if (!exists) {
          return prevActions
        }

        if (
          view === 'List' &&
          updatedAction.statut?.code !== 'NOUVELLE' &&
          updatedAction.statut?.code !== 'ENCOURS'
        ) {
          return prevActions.filter((item) => String(item.id) !== updatedActionId)
        }

        return prevActions.map((item) =>
          String(item.id) === updatedActionId ? updatedAction : item
        )
      })

      actionsCache.current.forEach((cacheEntry, key) => {
        const isListCache = key.includes('"view":"List"')
        if (
          isListCache &&
          updatedAction.statut?.code !== 'NOUVELLE' &&
          updatedAction.statut?.code !== 'ENCOURS'
        ) {
          const newData = cacheEntry.data.filter((item) => String(item.id) !== updatedActionId)
          actionsCache.current.set(key, {
            ...cacheEntry,
            data: newData,
            total: Math.max(0, cacheEntry.total - 1),
          })

          return
        }

        const exists = cacheEntry.data.some((item) => String(item.id) === updatedActionId)
        if (!exists) {
          return
        }

        const newData = cacheEntry.data.map((item) =>
          String(item.id) === updatedActionId ? updatedAction : item
        )

        actionsCache.current.set(key, {
          ...cacheEntry,
          data: newData,
        })
      })
    },
    [view]
  )

  const removeActionEverywhere = useCallback((actionId: number | string) => {
    const id = String(actionId)

    // State
    setActions((prevActions) => prevActions.filter((item) => String(item.id) !== id))

    // Cache
    actionsCache.current.forEach((cacheEntry, key) => {
      const exists = cacheEntry.data.some((item) => String(item.id) === id)

      if (!exists) {
        return
      }

      const newData = cacheEntry.data.filter((item) => String(item.id) !== id)

      actionsCache.current.set(key, {
        ...cacheEntry,
        data: newData,
        total: Math.max(0, cacheEntry.total - 1),
      })
    })
  }, [])

  const fetchActions = useCallback(async () => {
    const cachedData = actionsCache.current.get(cacheKey)

    if (cachedData) {
      setActions(cachedData.data)
      setNombreDePages(cachedData.pages)
      setTotalPages(cachedData.total)
      setCurrentPage(cachedData.page)

      return
    }

    const requestId = ++requestIdRef.current
    setLoading(true)
    try {
      const actionsRequest = await getAllActions(
        currentPage,
        filters.allSearch,
        apiStatut,
        filters.type,
        filters.origine,
        filters.dateMin,
        filters.dateMax,
        apiMesActions,
        view
      )

      // Une requête plus récente existe déjà
      if (requestId !== requestIdRef.current) {
        return
      }

      const data = actionsRequest?.data || []
      const pages = actionsRequest?.pages || 0
      const total = actionsRequest?.total || 0
      const page = actionsRequest?.page || currentPage
      actionsCache.current.set(cacheKey, {
        data,
        pages,
        total,
        page,
      })
      setActions(data)
      setNombreDePages(pages)
      setTotalPages(total)
      setCurrentPage(page)
    } catch (error) {
      console.error('Erreur lors de la récupération des actions', error)
      if (requestId === requestIdRef.current) {
        setActions([])
        setNombreDePages(0)
        setTotalPages(0)
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false)
      }
    }
  }, [cacheKey, currentPage, filters, apiStatut, apiMesActions, view])

  useEffect(() => {
    fetchActions()
  }, [fetchActions])

  const getStatutActionsData = useCallback(async () => {
    try {
      const response = await getStatutActions()

      if (response?.status === 200 && response?.data !== undefined) {
        const transformedData = response.data.map((item) => ({
          value: item.code,
          label: item.libelle,
        }))

        setStatutActions(transformedData)
      }
    } catch (error) {
      console.error('Erreur récupération statuts', error)
    }
  }, [])

  const getTypesActionsData = useCallback(async () => {
    try {
      const response = await getTypeActions()

      if (response?.status === 200 && response?.data !== undefined) {
        const transformedData = response.data.map((item) => ({
          value: item.code,
          label: item.libelle,
        }))

        setTypeActions(transformedData)
      }
    } catch (error) {
      console.error('Erreur récupération types', error)
    }
  }, [])

  useEffect(() => {
    getStatutActionsData()
    getTypesActionsData()
  }, [getStatutActionsData, getTypesActionsData])

  const OnchangeOptions = (value: string, critere: keyof SearchFilters) => {
    const nextValues = {
      ...formik.values,
      [critere]: value,
    }

    formik.setFieldValue(critere, value)
    setCurrentPage(1)
    setFilters({
      allSearch: nextValues.allSearch || '',
      statut: nextValues.statut || '',
      type: nextValues.type || '',
      origine: nextValues.origine || '',
      dateMin: nextValues.dateMin || '',
      dateMax: nextValues.dateMax || '',
    })
  }

  const handleSearch = () => {
    setCurrentPage(1)
    setFilters({
      allSearch: formik.values.allSearch || '',
      statut: formik.values.statut || '',
      type: formik.values.type || '',
      origine: formik.values.origine || '',
      dateMin: formik.values.dateMin || '',
      dateMax: formik.values.dateMax || '',
    })
  }

  const handleMesActionsChange = (checked: boolean) => {
    setCurrentPage(1)
    setMesActions(checked)
  }

  const handlePageChange = (page: number) => {
    if (page === currentPage) {
      return
    }
    setCurrentPage(page)
  }

  const handleViewChange = (newView: ViewType) => {
    if (newView === view) {
      return
    }

    setCurrentPage(1)

    if (newView !== 'Table') {
      setFilters(prevF => ({
        ...prevF,
        statut: '',
        dateMin: '',
        dateMax: '',
      }));

      setDateMin(null)
      setDateMax(null)
    } else {
      setFilters(prevF => ({
        ...prevF,
        mesActions: false
      }))
      setMesActions(false)
    }

    setView(newView)
  }

  useEffect(() => {
    formik.setFieldValue('dateMin', dateMin)
    formik.setFieldValue('dateMax', dateMax)
  }, [dateMin, dateMax])

  return (
    <>
      {shouldDisplayFonctionnalite(currentUser, 'VOIR ACTION') && (
        <div className='row g-5 g-xl-8'>
          <div className='col-xxl-12'>
            <div className='card mb-5 mb-xl-8'>
              {/* ==================================================
                  HEADER
              ================================================== */}

              <div className='card-header border-0 pt-5 d-flex'>
                <div>
                  <ul className='nav mt-5 mb-5 mx-2'>
                    {/* LISTE */}

                    <li className='nav-item'>
                      <a
                        href='#'
                        className={`nav-link btn btn-sm btn-color-muted btn-active btn-active-light-primary fw-bold px-4 me-1 ${
                          view === 'List' ? 'active' : ''
                        }`}
                        onClick={(e) => {
                          e.preventDefault()

                          handleViewChange('List')
                        }}
                      >
                        Liste
                      </a>
                    </li>

                    {/* KANBAN */}
                    <li className='nav-item'>
                      <a
                        href='#'
                        className={`nav-link btn btn-sm btn-color-muted btn-active btn-active-light-primary fw-bold px-4 me-1 ${
                          view === 'Kanban' ? 'active' : ''
                        }`}
                        onClick={(e) => {
                          e.preventDefault()

                          handleViewChange('Kanban')
                        }}
                      >
                        Kanban
                      </a>
                    </li>

                    {/* TABLEAU */}
                    <li className='nav-item'>
                      <a
                        href='#'
                        className={`nav-link btn btn-sm btn-color-muted btn-active btn-active-light-primary fw-bold px-4 ${
                          view === 'Table' ? 'active' : ''
                        }`}
                        onClick={(e) => {
                          e.preventDefault()
                          handleViewChange('Table')
                        }}
                      >
                        Tableau
                      </a>
                    </li>
                  </ul>
                </div>

                {/* AJOUTER */}
                {shouldDisplayFonctionnalite(currentUser, 'AJOUTER ACTION') && (
                  <div
                    className='card-toolbar ms-lg-auto'
                    data-bs-toggle='tooltip'
                    data-bs-placement='top'
                    data-bs-trigger='hover'
                    title='Créer une action'
                  >
                    <Link
                      to='/mes-actions/ajouter'
                      state={{view: view, filters: {...filters, mesActions: mesActions}}}
                      className='btn btn-sm btn-light-primary'
                    >
                      <KTIcon iconName='plus' className='fs-3' />
                      Créer une action
                    </Link>
                  </div>
                )}
              </div>

              <div className='card-header border-0 pt-5 pb-5'>
                <form
                  className='w-100'
                  id='searchForm'
                  onSubmit={(e) => {
                    e.preventDefault()

                    handleSearch()
                  }}
                >
                  <div className='row gy-3'>
                    {/* STATUT */}
                    {view === 'Table' && (
                      <div className='col-lg-2 col-sm-12'>
                        <select
                          name='statut'
                          id='statut'
                          value={formik.values.statut}
                          className='form-select form-select-lg fw-bold'
                          onChange={(e) => OnchangeOptions(e.target.value, 'statut')}
                        >
                          <option value=''>Statut</option>

                          {statutActions.length ? (
                            statutActions.map((statut, index) => (
                              <option key={statut.value || index} value={statut.value}>
                                {statut.label}
                              </option>
                            ))
                          ) : (
                            <option value=''>Aucun statut trouvé</option>
                          )}
                        </select>
                      </div>
                    )}

                    {/* TYPE */}
                    <div className='col-lg-2 col-sm-12'>
                      <select
                        name='type'
                        id='type'
                        value={formik.values.type}
                        className='form-select form-select-lg fw-bold'
                        onChange={(e) => OnchangeOptions(e.target.value, 'type')}
                      >
                        <option value=''>Type</option>

                        {typeActions.length ? (
                          typeActions.map((type, index) => (
                            <option key={type?.value || index} value={type?.value}>
                              {type?.label}
                            </option>
                          ))
                        ) : (
                          <option value=''>Aucun type trouvé</option>
                        )}
                      </select>
                    </div>

                    {/* ORIGINE */}
                    <div className='col-lg-2 col-sm-12'>
                      <select
                        name='origine'
                        id='origine'
                        value={filters.origine}
                        className='form-select form-select-lg fw-bold'
                        onChange={(e) => OnchangeOptions(e.target.value, 'origine')}
                      >
                        {origineFiltreMesActions.map((origine, index) => (
                          <option key={origine?.value || index} value={origine?.value}>
                            {origine?.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* DATES */}
                    {view === 'Table' && (
                      <>
                        <div className='col-lg-2 col-sm-12'>
                          <DateInput
                            fieldName='dateMin'
                            inputClassName='form-control'
                            setter={setDateMin}
                            formik={formik}
                            placeholder='Date Min'
                            customHeight={true}
                            value={dateMin}
                          />
                        </div>

                        <div className='col-lg-2 col-sm-12'>
                          <DateInput
                            fieldName='dateMax'
                            inputClassName='form-control'
                            setter={setDateMax}
                            formik={formik}
                            placeholder='Date Max'
                            customHeight={true}
                            value={dateMax}
                          />
                        </div>
                      </>
                    )}

                    {/* MES ACTIONS */}
                    {(view === 'List' || view === 'Kanban') && (
                      <div className='col-lg-2 col-sm-12 d-flex align-items-center'>
                        <label className='form-check form-check-custom form-check-solid'>
                          <input
                            className='form-check-input'
                            type='checkbox'
                            name='mesActions'
                            checked={mesActions}
                            onChange={(e) => handleMesActionsChange(e.target.checked)}
                          />
                          <span className='form-check-label fw-bold'>Mes actions uniquement</span>
                        </label>
                      </div>
                    )}

                    {/* RECHERCHE */}
                    <div className='col-lg-2 col-sm-12 ms-lg-auto'>
                      <div className='input-group'>
                        <input
                          type='text'
                          className='form-control h-45px'
                          {...formik.getFieldProps('allSearch')}
                          name='allSearch'
                          placeholder='Rechercher...'
                        />

                        <button
                          type='submit'
                          className='btn btn-light border-0 input-group-append h-100'
                        >
                          <i className='fas fa-search text-secondary fs-1'></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>

              {/* RESULTATS */}
              <div
                className='d-flex align-items-center gap-2'
                style={{
                  padding: '8px 2.25rem 23px',
                }}
              >
                <i className='bi bi-info-square'></i>

                <span>{totalPages} résultat(s)</span>
              </div>
            </div>

            <div className='card mb-5'>
              {loading ? (
                <LoadingData />
              ) : view === 'List' ? (
                <ListView
                  actions={actions}
                  currentUser={currentUser}
                  view={view}
                  filters={{...filters, mesActions: mesActions}}
                  setActions={setActions}
                />
              ) : view === 'Kanban' ? (
                <KanbanView
                  actions={actions}
                  currentUser={currentUser}
                  view={view}
                  filters={{...filters, mesActions: mesActions}}
                  setActions={setActions}
                  updateActionEverywhere={updateActionEverywhere}
                  removeActionEverywhere={removeActionEverywhere}
                />
              ) : (
                <TableView
                  actions={actions}
                  currentUser={currentUser}
                  view={view}
                  setActions={setActions}
                  filters={{...filters, mesActions: mesActions}}
                />
              )}

              {!loading && totalPages > 0 && (
                <div className='mt-3 mb-3'>
                  <Pagination
                    totalPages={totalPages}
                    nombreDePages={nombreDePages}
                    currentPage={currentPage}
                    setCurrentPage={handlePageChange}
                    locationState={0}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export {MesActionsListPage}
